import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import PanelResult from './PanelResult';
import {
  CK_ROUTES,
  CK_PERMISSIONS,
  apiMessage,
  type ReplenishmentPar,
  type ReplenishmentPlanRow,
  type WarehouseRef,
} from '../centralKitchenApi';
import { formatQuantity } from '../lib';

interface ItemOption {
  id: string;
  name: string;
  sku?: string | null;
}

/**
 * Par levels, and the plan derived from them: par minus on-hand minus what is
 * already in transit — so a shortfall the plan reports never double-counts
 * stock a transfer has already put on the road for this store.
 */
export default function ReplenishmentPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();

  const [newOpen, setNewOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [itemId, setItemId] = useState('');
  const [parLevel, setParLevel] = useState('');
  const [reorderPoint, setReorderPoint] = useState('');
  const [busy, setBusy] = useState(false);

  const pars = useApi<unknown>(can(CK_PERMISSIONS.replenishmentView) ? CK_ROUTES.pars : null, { isActive: true });
  const { rows: parRows } = rowsOf<ReplenishmentPar>(pars.data);

  const plan = useApi<unknown>(can(CK_PERMISSIONS.replenishmentView) ? CK_ROUTES.plan : null);
  const { rows: planRows } = rowsOf<ReplenishmentPlanRow>(plan.data);

  const warehouses = useApi<unknown>(newOpen ? CK_ROUTES.warehouses : null, { isActive: true, limit: 200 });
  const { rows: warehouseRows } = rowsOf<WarehouseRef>(warehouses.data);
  const items = useApi<unknown>(newOpen ? CK_ROUTES.items : null, { limit: 300 });
  const { rows: itemRows } = rowsOf<ItemOption>(items.data);

  const reload = () => {
    pars.reload();
    plan.reload();
  };

  const create = async () => {
    if (!warehouseId || !itemId || !parLevel.trim()) return;
    setBusy(true);
    try {
      await api.post(CK_ROUTES.pars, {
        warehouseId,
        inventoryItemId: itemId,
        parLevel: parLevel.trim(),
        reorderPoint: reorderPoint.trim() || undefined,
      });
      toast.success(t('centralKitchen.replenishment.saved'));
      setNewOpen(false);
      setWarehouseId('');
      setItemId('');
      setParLevel('');
      setReorderPoint('');
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (par: ReplenishmentPar) => {
    const ok = await confirm({
      title: t('centralKitchen.replenishment.removeTitle'),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(CK_ROUTES.par(par.id));
      toast.success(t('centralKitchen.replenishment.removed'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('centralKitchen.replenishment.pars')}</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reload}>
              <Icon as={RefreshCw} />
              {t('common.refresh')}
            </Button>
            {can(CK_PERMISSIONS.replenishmentManage) && (
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Icon as={Plus} />
                {t('centralKitchen.replenishment.new')}
              </Button>
            )}
          </div>
        </div>
        <TableWrap>
          <PanelResult
            loading={pars.loading}
            error={pars.error}
            count={parRows.length}
            onRetry={pars.reload}
            subject={t('centralKitchen.replenishment.subject')}
            emptyTitle={t('centralKitchen.replenishment.emptyTitle')}
            emptyHint={t('centralKitchen.replenishment.emptyHint')}
          >
            <Table>
              <THead>
                <TR>
                  <TH>{t('centralKitchen.replenishment.columns.warehouse')}</TH>
                  <TH>{t('centralKitchen.replenishment.columns.item')}</TH>
                  <TH className="text-end">{t('centralKitchen.replenishment.columns.par')}</TH>
                  <TH className="text-end">{t('centralKitchen.replenishment.columns.reorderPoint')}</TH>
                  <TH />
                </TR>
              </THead>
              <tbody>
                {parRows.map((par) => (
                  <TR key={par.id}>
                    <TD>{par.warehouse?.name ?? par.warehouseId}</TD>
                    <TD>{par.inventoryItem?.name ?? par.inventoryItemId}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(par.parLevel, i18n.language)}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(par.reorderPoint, i18n.language)}</TD>
                    <TD>
                      {can(CK_PERMISSIONS.replenishmentManage) && (
                        <Button variant="ghost" size="sm" onClick={() => remove(par)}>
                          <Icon as={Trash2} size="sm" className="text-red-600 dark:text-red-400" />
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </PanelResult>
        </TableWrap>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('centralKitchen.replenishment.plan')}</h2>
        <TableWrap>
          <PanelResult
            loading={plan.loading}
            error={plan.error}
            count={planRows.length}
            onRetry={plan.reload}
            subject={t('centralKitchen.replenishment.planSubject')}
            emptyTitle={t('centralKitchen.replenishment.planEmptyTitle')}
            emptyHint={t('centralKitchen.replenishment.planEmptyHint')}
          >
            <Table>
              <THead>
                <TR>
                  <TH>{t('centralKitchen.replenishment.columns.item')}</TH>
                  <TH className="text-end">{t('centralKitchen.replenishment.columns.par')}</TH>
                  <TH className="text-end">{t('centralKitchen.replenishment.onHand')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.inTransitTitle')}</TH>
                  <TH className="text-end">{t('centralKitchen.replenishment.shortfall')}</TH>
                </TR>
              </THead>
              <tbody>
                {planRows.map((row, i) => (
                  <TR key={`${row.warehouseId}-${row.inventoryItemId}-${i}`}>
                    <TD>{row.inventoryItem?.name ?? row.inventoryItemId}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(row.parLevel, i18n.language)}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(row.onHand, i18n.language)}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(row.inTransit, i18n.language)}</TD>
                    <TD
                      className={`text-end tabular-nums ${
                        Number(row.shortfall) > 0 ? 'font-medium text-amber-600 dark:text-amber-400' : ''
                      }`}
                    >
                      {formatQuantity(row.shortfall, i18n.language)}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </PanelResult>
        </TableWrap>
      </section>

      {newOpen && (
        <Dialog
          open
          onClose={() => setNewOpen(false)}
          title={t('centralKitchen.replenishment.new')}
          className="max-w-sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setNewOpen(false)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button onClick={create} disabled={busy || !warehouseId || !itemId || !parLevel.trim()}>
                {t('common.save')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label={t('centralKitchen.replenishment.columns.warehouse')} required controlId="ck-par-warehouse">
              <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">{t('centralKitchen.transfers.chooseWarehouse')}</option>
                {warehouseRows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('centralKitchen.replenishment.columns.item')} required controlId="ck-par-item">
              <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                <option value="">{t('centralKitchen.transfers.chooseItem')}</option>
                {itemRows.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name} {it.sku ? `(${it.sku})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('centralKitchen.replenishment.columns.par')} required controlId="ck-par-level">
              <Input inputMode="decimal" value={parLevel} onChange={(e) => setParLevel(e.target.value)} />
            </Field>
            <Field
              label={t('centralKitchen.replenishment.columns.reorderPoint')}
              hint={t('centralKitchen.replenishment.reorderPointHint')}
              controlId="ck-par-reorder"
            >
              <Input inputMode="decimal" value={reorderPoint} onChange={(e) => setReorderPoint(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
